import {
  App,
  fullEndpoints,
  fullRoutes,
  routes,
} from "@songtradr-sso/shared/common";
import {
  NormalizedException,
  createAxiosClient,
  redirect,
} from "@songtradr-sso/shared/frontend";

/** Axios Client */
export const Api = createAxiosClient({
  auth: { refresh: { redirect: () => Promise.resolve() } }, // sso-core doesn't require redirect
});

/** Forward Authentication Request
 *
 * Sends a request to forward the authentication and redirects the user
 * to the appropriate URL based on the response.
 *
 * @returns void
 */
export const forwardAuth = async () => {
  try {
    const result = await Api.request({
      method: "POST",
      url: fullEndpoints[App.Core].auth.forwardAuth,
    });

    await redirect(result.data.data ?? fullRoutes[App.Account].base);
  } catch (err) {
    const { code } = new NormalizedException(err, false);
    if (code === "ForwardAuth.WorkspaceMismatch") {
      window.location.reload();
      return;
    }
    throw err;
  }
};

/** Handle Multi-Factor Authentication (MFA)
 *
 * Checks response for MFA details, validates them, stores necessary details
 * in session storage, and returns the verification route if MFA is required.
 *
 * @param res - Response object containing MFA details
 *
 * @returns Verification route if MFA is required, otherwise undefined
 */
export const handleMfa = (res: { [key: string]: never }) => {
  // Check for MFA requirement...
  const { mfa_method, mfa_token, phone_digits } = res ?? {};
  if (!mfa_method || mfa_method === "none") return;

  // Validate provided MFA data...
  const args = [mfa_method, mfa_token].filter(Boolean);
  if (args.some((m) => typeof m !== "string"))
    throw new Error("Invalid MFA Response");

  if (mfa_method === "sms" && !phone_digits)
    throw new Error("Missing phone digits for SMS MFA");

  // Store MFA details in session storage...
  sessionStorage.setItem("auth_tmp", mfa_token);

  if (mfa_method === "sms")
    sessionStorage.setItem("phone_digits", phone_digits);

  // Return the verification route...
  const mfaRoutes = routes[App.Core].mfa;
  return mfaRoutes[mfa_method as keyof typeof mfaRoutes].verify;
};
